import { extractApiErrorMessage } from '@cartwheel/api'
import {
  type MedicaidMcoMappingResolveRequest,
  myTasksQueryKey,
  type Task,
  taskByIdQueryKey,
  taskInsuranceVerificationContextOptions,
  tasksQueryKey,
  useTaskResolveMedicaidMcoMappingMutation,
} from '@cartwheel/api/v1'
import {
  Button,
  ErrorView,
  LoadingView,
  SegmentedControl,
  Textarea,
  useToast,
} from '@cartwheel/components'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { InsuranceCard } from './insurance-card'
import { ResolutionSection } from './resolution-section'
import { TaskDescription } from './shared/task-workspace-primitives'
import { taskAnalyticsContext, trackTaskResourceLink } from './task-analytics'
import { TaskCompletionBanner } from './task-completion-banner'

type ResolutionMethod = MedicaidMcoMappingResolveRequest['outcome']

const HELPER_TEXT: Record<ResolutionMethod, string> = {
  mco_mapped:
    'Add an internal billing note (optional) describing how you set the MCO in Apero, then mark the task complete.',
  mco_not_mappable:
    "Add a note explaining why the MCO can't be mapped (e.g., non-Medicaid commercial plan, Medicaid doesn't cover behavioral health) before closing the task.",
}

const OUTCOME_LABEL: Record<ResolutionMethod, string> = {
  mco_mapped: 'Mapped',
  mco_not_mappable: 'Not mappable',
}

function isResolutionMethod(value: unknown): value is ResolutionMethod {
  return value === 'mco_mapped' || value === 'mco_not_mappable'
}

export function MedicaidMcoMappingWorkspace({
  task,
  canWork = true,
  onClose,
}: {
  task: Task
  canWork?: boolean
  onClose: () => void
}) {
  const [resolutionMethod, setResolutionMethod] = useState<ResolutionMethod>('mco_mapped')
  const [note, setNote] = useState('')
  const noteId = useId()
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data, isPending, isError } = useQuery(
    taskInsuranceVerificationContextOptions({ path: { task_id: task.id } })
  )

  const resolveMutation = useTaskResolveMedicaidMcoMappingMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: myTasksQueryKey() })
      void queryClient.invalidateQueries({ queryKey: tasksQueryKey() })
      void queryClient.invalidateQueries({
        queryKey: taskByIdQueryKey({ path: { id: task.id } }),
      })
      toast.success({ description: 'Task resolved' })
      onClose()
    },
    onError: (error) => {
      toast.error({ description: extractApiErrorMessage(error, 'Failed to resolve task') })
    },
  })

  if (isPending) {
    return <LoadingView title="Loading task context…" />
  }

  if (isError || !data) {
    return <ErrorView title="Unable to load task context" message="Please close and try again." />
  }

  const isTerminal = task.status === 'completed' || task.status === 'canceled'
  const isPendingResolve = resolveMutation.isPending
  const rawOutcome = task.metadata?.['outcome']
  const outcomeLabel = isResolutionMethod(rawOutcome) ? OUTCOME_LABEL[rawOutcome] : undefined

  const handleMarkComplete = () => {
    resolveMutation.mutate({
      path: { task_id: task.id },
      body: { outcome: resolutionMethod, note: note.trim() || undefined },
    })
  }

  return (
    <div className="space-y-8">
      <TaskDescription task={task} />
      <InsuranceCard
        insuranceName={data.insurance?.insurance_name}
        policyHolderName={data.insurance?.policy_holder_name}
        memberId={data.insurance?.member_id}
        aperoUrl={data.apero_url}
        availityUrl={data.availity_url}
        mcoName={data.mco_name}
        planType={data.plan_type}
        showExternalLinks={canWork}
        onResourceClick={(destination) => trackTaskResourceLink(task, destination)}
      />

      {isTerminal ? (
        <TaskCompletionBanner task={task} outcomeLabel={outcomeLabel} />
      ) : canWork ? (
        <ResolutionSection>
          <SegmentedControl.Root
            value={resolutionMethod}
            onChange={(next) => {
              if (isResolutionMethod(next)) setResolutionMethod(next)
            }}
            track={{
              name: 'TASK_RESOLUTION_METHOD_SELECTED',
              properties: ({ value: resolutionMethod }) => ({
                ...taskAnalyticsContext(task),
                resolutionMethod,
              }),
            }}
          >
            <SegmentedControl.Option value="mco_mapped" disabled={isPendingResolve}>
              MCO mapped
            </SegmentedControl.Option>
            <SegmentedControl.Option value="mco_not_mappable" disabled={isPendingResolve}>
              Cannot map
            </SegmentedControl.Option>
          </SegmentedControl.Root>

          <p className="text-sm leading-[1.5] text-charcoal-50">{HELPER_TEXT[resolutionMethod]}</p>

          <div className="space-y-2">
            <label htmlFor={noteId} className="text-base font-semibold leading-[1.5] text-charcoal">
              Internal note
            </label>
            <Textarea
              id={noteId}
              value={note}
              rows={3}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Optional — pinned to the student activity log. Not shown to the family."
            />
          </div>

          <Button className="w-full" disabled={isPendingResolve} onClick={handleMarkComplete}>
            {isPendingResolve ? 'Saving…' : 'Mark complete'}
          </Button>
        </ResolutionSection>
      ) : null}
    </div>
  )
}
